export const SUPPORTED_LOCALES = ["en", "es"] as const;
export const LOCALES = SUPPORTED_LOCALES;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];
export type AppLocale = SupportedLocale;
export type OutputLocale = SupportedLocale;

export interface UserLanguagePreferences {
  appLocale: AppLocale;
  defaultOutputLocale: OutputLocale;
}

export const DEFAULT_APP_LOCALE: AppLocale = "en";
export const DEFAULT_LOCALE = DEFAULT_APP_LOCALE;
export const DEFAULT_OUTPUT_LOCALE: OutputLocale = "en";
export const APP_LOCALE_COOKIE_NAME = "lf_app_locale";
export const APP_LOCALE_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

export const USER_LANGUAGE_METADATA_KEYS = {
  appLocale: "preferred_language",
  defaultOutputLocale: "default_output_language",
} as const;

const normalizeLocaleToken = (value: string) =>
  value
    .split(",")[0]
    ?.split(";")[0]
    ?.trim()
    .split("-")[0]
    ?.toLowerCase() ?? "";

export const isSupportedLocale = (
  value: unknown
): value is SupportedLocale =>
  typeof value === "string" &&
  (SUPPORTED_LOCALES as readonly string[]).includes(value);

export const normalizeSupportedLocale = (
  value: unknown
): SupportedLocale | null => {
  if (typeof value !== "string" || value.trim().length === 0) {
    return null;
  }

  const normalized = normalizeLocaleToken(value);
  return isSupportedLocale(normalized) ? normalized : null;
};

export const resolveSupportedLocale = (
  value: unknown,
  fallback: SupportedLocale = DEFAULT_APP_LOCALE
): SupportedLocale => normalizeSupportedLocale(value) ?? fallback;

export const normalizeLocale = resolveSupportedLocale;

export const getUserLanguagePreferencesFromMetadata = (
  metadata: Record<string, unknown> | null | undefined
): UserLanguagePreferences => ({
  appLocale: resolveSupportedLocale(
    metadata?.[USER_LANGUAGE_METADATA_KEYS.appLocale],
    DEFAULT_APP_LOCALE
  ),
  defaultOutputLocale: resolveSupportedLocale(
    metadata?.[USER_LANGUAGE_METADATA_KEYS.defaultOutputLocale],
    DEFAULT_OUTPUT_LOCALE
  ),
});
